import styled from "@emotion/styled";
import assertNever from "assert-never";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { LocalDateTime } from "@js-joda/core";
import React from "react";
import { default as ClockAlertOutlineIcon } from "../../../public/assets/clock-alert-outline.svg?react";
import { default as ClockTimeFourOutlineIcon } from "../../../public/assets/clock-time-four-outline.svg?react";
import { default as DollarCircleIcon } from "../../../public/assets/dollar-circle.svg?react";
import { default as LeftArrowCircleIcon } from "../../../public/assets/left-arrow-circle.svg?react";
import { default as RightArrowCircleIcon } from "../../../public/assets/right-arrow-circle.svg?react";
import { InstaPayVisit, InstaPayVisitsData, VisitPaymentStatus } from "../../messages/Payroll";
import { STRINGS } from "../../strings";
import { globalize, translate } from "../../translate";
import { splitInstaPayVisitsByPeriod } from "../../utils/payrollUtils";
import { AppCard, AppCardContent } from "../Generic/AppCard";
import Flex from "../Generic/Flex";
import { getFormattedArrivalTime } from "../InstaPay/utils/get-instapay-arrival-time";
import Margin from "../Margin";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";

interface Props {
  data: InstaPayVisitsData;
  weekLabel: React.ReactNode;
  onClickHistory: () => void;
  onClickPreviousWeek: () => void;
  onClickNextWeek: () => void;
  onClickVisit: (visit: InstaPayVisit) => void;
  onClickGoBack: () => void;
}

export default function InstaPayHistoryScreen(props: Props) {
  const visits = splitInstaPayVisitsByPeriod({ visits: props.data.visits });

  return (
    <Scaffold
      title={<ScaffoldTitle label={translate(STRINGS.INSTAPAY_HISTORY)} />}
      onClickGoBack={props.onClickGoBack}
    >
      <Margin m={16}>
        <Flex direction="column" gap={16}>
          <AppCard>
            <AppCardContent>
              <Flex gap={16} direction="column">
                <Weeks
                  label={props.weekLabel}
                  onClickPreviousWeek={props.onClickPreviousWeek}
                  onClickNextWeek={props.onClickNextWeek}
                />

                {props.data.visits.length === 0 && (
                  <motion.div
                    key="no-visits"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <NoVisitsContainer>
                      {translate(STRINGS.IT_APPEARS_YOU_HAVE_NO_VISITS_THIS_WEEK)}
                    </NoVisitsContainer>
                  </motion.div>
                )}

                <VisitBlocks>
                  <LayoutGroup>
                    <AnimatePresence>
                      <VisitsBlock
                        key="past"
                        title={translate(STRINGS.RECENT_VISITS)}
                        visits={visits.past}
                        onClickVisit={props.onClickVisit}
                      />

                      <VisitsBlock
                        key="current"
                        title={translate(STRINGS.CURRENT_VISIT)}
                        visits={visits.current}
                        onClickVisit={props.onClickVisit}
                      />

                      <VisitsBlock
                        key="future"
                        isDisabled={true}
                        title={translate(STRINGS.UPCOMING_VISITS)}
                        visits={visits.future}
                        onClickVisit={props.onClickVisit}
                      />
                    </AnimatePresence>
                  </LayoutGroup>
                </VisitBlocks>
              </Flex>
            </AppCardContent>
          </AppCard>
        </Flex>
      </Margin>
    </Scaffold>
  );
}

const VisitsBlockRoot = styled(motion.div)<{ isDisabled: boolean }>`
  position: relative;
  overflow: hidden;
  opacity: ${(props) => (props.isDisabled ? 0.5 : undefined)};
`;

export const VisitBlocks = styled.div`
  ${VisitsBlockRoot} + ${VisitsBlockRoot} {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--tw-slate-200);
  }
`;

export const VisitsBlock = (props: {
  visits: InstaPayVisit[];
  title: string;
  isDisabled?: boolean;
  onClickVisit: (visit: InstaPayVisit) => void;
}) => {
  if (props.visits.length === 0) return <></>;

  return (
    <VisitsBlockRoot layout="position" isDisabled={props.isDisabled === true}>
      <VisitsHeading
        initial={{ opacity: 0, translateX: -20 }}
        animate={{ opacity: 1, translateX: 0 }}
        exit={{ opacity: 0, translateX: -20 }}
      >
        {props.title}
      </VisitsHeading>

      <VisitsList>
        {props.visits.map((visit) => (
          <VisitItem
            {...visit}
            key={`${visit.id}`}
            payRate={((visit.payRate ?? 0) / 100).toFixed(2)}
            onClick={() => props.onClickVisit(visit)}
          />
        ))}
      </VisitsList>
    </VisitsBlockRoot>
  );
};

const VisitsHeading = styled(motion.h2)`
  font-size: 14px;
  font-weight: 500;
  color: var(--tw-slate-500);
  width: fit-content;
  border-radius: 4px;
`;

const VisitItemRoot = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 8px;

  &: [data-is-opacity= "true"] {
    opacity: 0.75;
  }
`;

export const VisitsList = styled.div`
  gap: 16px;
  display: flex;
  flex-direction: column;

  ${VisitItemRoot} + ${VisitItemRoot} {
    padding-top: 16px;
    border-top: 1px solid var(--tw-slate-200);
  }
`;

const VisitTime = styled.h3`
  margin: 0;
  font-size: 16px;
`;

const VisitStatusRoot = styled.div<{ color: Color }>`
  background-color: ${(props) => `var(--tw-${props.color})`};
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 12px;
  color: #fff;
  white-space: nowrap;
`;

type Color =
  | `slate-${900 | 800 | 700 | 400 | 300 | 200 | 50}`
  | `green-600`
  | `yellow-${500}`
  | `red-${500}`
  | `blue-${700 | 600 | 500}`;

const VisitStatus = (props: {
  startTime: LocalDateTime;
  clockInTime: LocalDateTime | null;
  clockOutTime: LocalDateTime | null;
  status: VisitPaymentStatus;
}) => {
  switch (props.status.type) {
    case "Paid":
      return <VisitStatusRoot color="green-600">{translate(STRINGS.PAID)}</VisitStatusRoot>;
    case "InstaPayInProgress":
      return <VisitStatusRoot color="green-600">{translate(STRINGS.IN_PROGRESS)}</VisitStatusRoot>;
    case "PendingIssues":
      return <VisitStatusRoot color="red-500">{translate(STRINGS.PENDING_ISSUES)}</VisitStatusRoot>;
    case "EligibleInstaPay":
      return <VisitStatusRoot color="blue-600">{translate(STRINGS.GET_INSTAPAY)}</VisitStatusRoot>;
    case "MissingClockTimes":
      return (
        <VisitStatusRoot color="yellow-500">
          <ClockAlertOutlineIcon height={16} width={16} />
          {translate(STRINGS.MISSING_CLOCKS)}
        </VisitStatusRoot>
      );
    case "VisitNotCompleted":
      return (
        <VisitStatusRoot color="yellow-500">
          <ClockAlertOutlineIcon height={16} width={16} />
          {translate(STRINGS.COMPLETE_VISIT)}
        </VisitStatusRoot>
      );
    case "TechnicalIssue":
      return (
        <VisitStatusRoot color="yellow-500">
          {translate(STRINGS.INSTAPAY_TECHNICAL_ISSUE)}
        </VisitStatusRoot>
      );
    case "RegularPay":
      return <VisitStatusRoot color="slate-400">{translate(STRINGS.REGULAR_PAY)}</VisitStatusRoot>;
    default:
      assertNever(props.status);
  }
};

const VisitDetailRoot = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  font-weight: 700;
  font-size: 14px;
`;

const VisitDetail = (props: { icon: JSX.Element; value: string }) => {
  return (
    <VisitDetailRoot>
      {props.icon}
      <div>{props.value}</div>
    </VisitDetailRoot>
  );
};

const VisitPrice = styled.h4`
  margin: 0;
  font-weight: 700;
`;

const VisitPaidInfo = styled.h5`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: var(--tw-slate-400);
`;

const VisitPriceInfo = (props: { status: VisitPaymentStatus; amountInDollars: string }) => {
  switch (props.status.type) {
    case "EligibleInstaPay":
    case "InstaPayInProgress":
    case "VisitNotCompleted":
    case "RegularPay":
    case "PendingIssues":
    case "MissingClockTimes":
    case "TechnicalIssue":
      return <VisitPrice>${props.amountInDollars}</VisitPrice>;
    case "Paid":
      return (
        <VisitPaidInfo>
          {translate(STRINGS.PAID)} {`$${(props.status.amount / 100).toFixed(2)}`}
        </VisitPaidInfo>
      );
    default:
      assertNever(props.status);
  }
};

export const VisitItem = (props: {
  startTime: LocalDateTime;
  endTime: LocalDateTime;
  duration: string;
  clockInTime: LocalDateTime | null;
  clockOutTime: LocalDateTime | null;
  status: VisitPaymentStatus;
  payRate: string;
  totalAmount: number;
  onClick: (visit: { status: VisitPaymentStatus }) => void;
  isOpacity?: boolean;
}) => {
  const startTimeF = globalize().formatLocalDateTime(props.clockInTime ?? props.startTime, {
    datetime: "short",
  });

  const endTimeF = globalize().formatLocalDateTime(props.clockOutTime ?? props.endTime, {
    time: "short",
  });

  const balanceInDollars = (props.totalAmount / 100).toFixed(2);

  const isFutureVisit = props.startTime.isAfter(LocalDateTime.now());

  return (
    <VisitItemRoot
      initial={{ opacity: 0, translateX: -30 }}
      animate={{ opacity: props.isOpacity === true ? 0.5 : 1, translateX: 0 }}
      transition={{ delay: Math.random() * 0.3 }}
      onClick={() => props.onClick({ status: props.status })}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <VisitTime>
          {startTimeF} - {endTimeF}
        </VisitTime>

        {!isFutureVisit && (
          <VisitStatus
            startTime={props.startTime}
            status={props.status}
            clockInTime={props.clockInTime}
            clockOutTime={props.clockOutTime}
          />
        )}
      </Flex>

      <Flex justifyContent="space-between" alignItems="center">
        {props.status.type === "InstaPayInProgress" ? (
          <VisitDetail
            icon={<ClockTimeFourOutlineIcon height={16} width={16} />}
            value={translate(STRINGS.DUE_BY_DATETIME).replace(
              "{DATETIME}",
              getFormattedArrivalTime(props.status.requestedAt)
            )}
          />
        ) : (
          <Flex gap={8}>
            <VisitDetail
              icon={<ClockTimeFourOutlineIcon height={16} width={16} />}
              value={props.duration}
            />

            <VisitDetail
              icon={<DollarCircleIcon height={16} width={16} color="var(--tw-yellow-500)" />}
              value={`${props.payRate}/h`}
            />
          </Flex>
        )}

        <VisitPriceInfo amountInDollars={balanceInDollars} status={props.status} />
      </Flex>
    </VisitItemRoot>
  );
};

const NoVisitsContainer = styled.div`
  text-align: center;
  padding: 16px;
  color: var(--tw-slate-500);
`;

const WeeksRoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px;
  border-radius: 32px;
  background-color: var(--primary-softest-color);
`;

const WeekLabel = styled.div`
  padding: 8px;
  font-weight: 600;
`;

const WeeksButton = styled.div`
  display: flex;
  padding: 8px;
`;

const Weeks = (props: {
  label: React.ReactNode;
  onClickPreviousWeek: () => void;
  onClickNextWeek: () => void;
}) => {
  return (
    <WeeksRoot>
      <WeeksButton>
        <LeftArrowCircleIcon color="var(--blue-dark)" onClick={props.onClickPreviousWeek} />
      </WeeksButton>
      <WeekLabel>{props.label}</WeekLabel>
      <WeeksButton>
        <RightArrowCircleIcon color="var(--blue-dark)" onClick={props.onClickNextWeek} />
      </WeeksButton>
    </WeeksRoot>
  );
};
